/**
 * AppICEMAP — Observable State Store
 * Lightweight reactive store (~300 LOC) using Proxy-based observation.
 *
 * Manages slices:
 *   reports[]      — active report objects in viewport
 *   categories[]   — category definitions from Directus
 *   user           — current user profile + session
 *   filters        — selected categories, search text
 *   theme          — current theme ('day' | 'night')
 *   offline        — connection state
 *   ui             — bottom sheet state, selected report, loading flags
 *
 * Usage:
 *   import { state, subscribe, dispatch, getState } from './store/index.js';
 *   subscribe('reports', (newVal) => { ... });
 *   dispatch('SET_REPORTS', reportsArray);
 */

/** @type {Map<string, Set<Function>>} */
const subscribers = new Map();

/* ── Initial State ───────────────────────────────────────────────────────── */

/** localStorage key for the nearby-radius preference */
const RADIUS_KEY = "appicemap-radius-km";

/**
 * Read the saved radius. Kept out of the reports slice on purpose: it is a
 * preference, not data, and must survive reloads.
 * @returns {number|null} km, or null for "sin límite"
 */
function loadRadius() {
	try {
		const raw = localStorage.getItem(RADIUS_KEY);
		if (raw === null || raw === "null") return null;
		const km = Number(raw);
		return Number.isFinite(km) && km > 0 ? km : null;
	} catch {
		return null;
	}
}

/**
 * Persist the radius preference.
 * @param {number|null} km
 */
export function saveRadius(km) {
	try {
		localStorage.setItem(RADIUS_KEY, km === null ? "null" : String(km));
	} catch {
		/* private mode — the setting just won't survive the reload */
	}
}

const initialState = {
	/** @type {Array} Active reports in current viewport */
	reports: [],

	/** @type {Array} Category definitions from Directus */
	categories: [],

	/** @type {Object | null} Current user profile */
	user: null,

	/** @type {Object} Active filters */
	filters: {
		selectedCategories: [], // slugs
		searchText: "",
		/** @type {number|null} km around you for the list; null = sin límite */
		radiusKm: loadRadius(),
	},

	/** @type {'day' | 'night'} Current theme */
	theme: "day",

	/** @type {boolean} Connection state */
	offline: !navigator.onLine,

	/** @type {Object} UI state */
	ui: {
		/** @type {'collapsed' | 'peek' | 'expanded'} */
		bottomSheetState: "peek",
		/** @type {string | null} UUID of selected report */
		selectedReportId: null,
		/** @type {boolean} Map is loading */
		mapLoading: true,
		/** @type {boolean} Reports are loading */
		reportsLoading: false,
		/** @type {[number, number] | null} User GPS position */
		userLocation: null,
		/** @type {Date | null} Last successful data sync */
		lastSyncTime: null,
	},
};

/* ── Internal State ──────────────────────────────────────────────────────── */

const _state = { ...initialState };

/**
 * Deep clone helper — structuredClone with fallback.
 */
function clone(obj) {
	if (typeof structuredClone === "function") {
		return structuredClone(obj);
	}
	return JSON.parse(JSON.stringify(obj));
}

/* ── Reactive Proxy ──────────────────────────────────────────────────────── */

const handler = {
	get(target, prop) {
		return target[prop];
	},
	set(target, prop, value) {
		const old = target[prop];
		target[prop] = value;

		// Notify subscribers for this exact key
		const set = subscribers.get(prop);
		if (set) {
			for (const cb of set) {
				try {
					cb(value, old);
				} catch (e) {
					console.error(`[Store] Subscriber error for "${prop}":`, e);
				}
			}
		}

		// Also notify wildcard subscribers
		const wild = subscribers.get("*");
		if (wild) {
			for (const cb of wild) {
				try {
					cb(prop, value, old);
				} catch {
					/* swallow */
				}
			}
		}

		return true;
	},
};

/** Reactive state proxy — reads and writes trigger subscribers */
export const state = new Proxy(_state, handler);

/* ── Subscriptions ───────────────────────────────────────────────────────── */

/**
 * Subscribe to state changes on a specific key.
 * @param {string} key - State key to watch (e.g., 'reports', 'ui')
 * @param {Function} callback - (newValue, oldValue) => void
 * @returns {Function} unsubscribe
 */
export function subscribe(key, callback) {
	if (!subscribers.has(key)) {
		subscribers.set(key, new Set());
	}
	subscribers.get(key).add(callback);

	return () => {
		const set = subscribers.get(key);
		if (set) set.delete(callback);
	};
}

/* ── Dispatch ─────────────────────────────────────────────────────────────── */

/**
 * Dispatch an action to update state. Actions follow the pattern:
 *   SET_<SLICE>    — replace entire slice
 *   MERGE_<SLICE>  — shallow merge into slice
 *   ADD_<SLICE>    — push to array slice
 *   REMOVE_<SLICE> — filter from array slice
 *
 * @param {string} action - Action name
 * @param {*} payload - Data for the action
 */
export function dispatch(action, payload) {
	switch (action) {
		/* ── Reports ──────────────────────────────────────────────────── */
		case "SET_REPORTS":
			// Coerce: a non-array payload (an error object, a paginated
			// { data } envelope…) would otherwise reach every subscriber and
			// blow up on .map()/.filter().
			state.reports = Array.isArray(payload) ? payload : [];
			state.ui.reportsLoading = false;
			state.ui.lastSyncTime = new Date();
			break;

		case "MERGE_REPORT": {
			const idx = state.reports.findIndex((r) => r.id === payload.id);
			if (idx > -1) {
				const updated = [...state.reports];
				updated[idx] = { ...updated[idx], ...payload };
				state.reports = updated;
			}
			break;
		}

		case "ADD_REPORT": {
			if (!state.reports.find((r) => r.id === payload.id)) {
				state.reports = [payload, ...state.reports];
			}
			break;
		}

		case "REMOVE_REPORT":
			state.reports = state.reports.filter((r) => r.id !== payload);
			break;

		/* ── Categories ──────────────────────────────────────────────── */
		case "SET_CATEGORIES":
			state.categories = Array.isArray(payload) ? payload : [];
			break;

		/* ── User ────────────────────────────────────────────────────── */
		case "SET_USER":
			state.user = payload;
			break;

		case "CLEAR_USER":
			state.user = null;
			break;

		/* ── Filters ─────────────────────────────────────────────────── */
		case "SET_FILTER":
			state.filters = { ...state.filters, ...payload };
			break;

		case "TOGGLE_CATEGORY_FILTER": {
			const slug = payload;
			const sel = state.filters.selectedCategories;
			const exists = sel.includes(slug);
			state.filters = {
				...state.filters,
				selectedCategories: exists
					? sel.filter((s) => s !== slug)
					: [...sel, slug],
			};
			break;
		}

		case "CLEAR_CATEGORY_FILTERS":
			state.filters = {
				...state.filters,
				selectedCategories: [],
			};
			break;

		/* ── Theme ───────────────────────────────────────────────────── */
		case "SET_THEME":
			state.theme = payload;
			break;

		/* ── Offline ─────────────────────────────────────────────────── */
		case "SET_OFFLINE":
			state.offline = payload;
			break;

		/* ── UI ──────────────────────────────────────────────────────── */
		case "SET_UI":
			state.ui = { ...state.ui, ...payload };
			break;

		case "SET_BOTTOM_SHEET_STATE":
			state.ui = { ...state.ui, bottomSheetState: payload };
			break;

		case "SELECT_REPORT":
			state.ui = {
				...state.ui,
				selectedReportId: payload,
			};
			break;

		case "SET_MAP_LOADING":
			state.ui = { ...state.ui, mapLoading: payload };
			break;

		default:
			console.warn(`[Store] Unknown action: ${action}`);
	}
}

/* ── Snapshot ─────────────────────────────────────────────────────────────── */

/**
 * Get a deep-cloned snapshot of the entire state.
 * @returns {object}
 */
export function getState() {
	return clone(_state);
}

/**
 * Get a specific slice value directly (non-reactive read).
 */
export function getSlice(key) {
	return _state[key];
}
